import {useEffect, useState} from 'react'
import type {Webhook} from '../../../types'
import {getUrlValidationError} from '../validation'

interface UseWebhookFormOptions {
	editingWebhook: Webhook | undefined
	editingWebhookId: string | null
	isCreateMode: boolean
	hasForm: boolean
	canShowForm: boolean
}

interface WebhookFormState {
	url: string
	apiKey: string
	selectedEvents: string[]
	urlErrorMessage: string | null
	apiKeyErrorMessage: string | null
	formPopulatedForEdit: boolean
}

export function useWebhookForm({
																 editingWebhook,
																 editingWebhookId,
																 isCreateMode,
																 hasForm,
																 canShowForm
															 }: UseWebhookFormOptions) {
	const [state, setState] = useState<WebhookFormState>({
		url: '',
		apiKey: '',
		selectedEvents: [],
		urlErrorMessage: null,
		apiKeyErrorMessage: null,
		formPopulatedForEdit: false
	})

	useEffect(() => {
		if (editingWebhookId && !isCreateMode) {
			setState(prev => ({
				...prev,
				formPopulatedForEdit: false,
				url: '',
				apiKey: '',
				selectedEvents: [],
				urlErrorMessage: null,
				apiKeyErrorMessage: null
			}))
		}
	}, [editingWebhookId, isCreateMode])

	useEffect(() => {
		if (editingWebhookId && !isCreateMode && editingWebhook && state.formPopulatedForEdit) {
			setState(prev => ({ ...prev, formPopulatedForEdit: false }))
		}
	}, [editingWebhook])

	useEffect(() => {
		if (
			editingWebhook &&
			!isCreateMode &&
			editingWebhookId &&
			!state.formPopulatedForEdit
		) {
			const events = Array.isArray(editingWebhook.events)
				? editingWebhook.events
					.map(e => (String(e)))
					.filter(e => e)
				: []

			setState(prev => ({
				...prev,
				url: editingWebhook.uri || '',
				apiKey: editingWebhook.api_key || '',
				selectedEvents: events,
				formPopulatedForEdit: true
			}))
		}
	}, [
		editingWebhook,
		editingWebhookId,
		isCreateMode,
		state.formPopulatedForEdit
	])

	useEffect(() => {
		if (hasForm && !canShowForm) {
			setState(prev => ({...prev, urlErrorMessage: null}))
		}
	}, [hasForm, canShowForm])

	const resetForm = () => {
		setState({
			url: '',
			apiKey: '',
			selectedEvents: [],
			urlErrorMessage: null,
			apiKeyErrorMessage: null,
			formPopulatedForEdit: false
		})
	}

	const setUrl = (value: string) => {
		setState(prev => ({
			...prev,
			url: value,
			urlErrorMessage: null
		}))
	}

	const setApiKey = (value: string) => {
		setState(prev => ({
			...prev,
			apiKey: value
		}))
	}

	const setSelectedEvents = (events: string[]) => {
		setState(prev => ({...prev, selectedEvents: events}))
	}

	const toggleEvent = (eventId: string) => {
		setState(prev => ({
			...prev,
			selectedEvents: prev.selectedEvents.includes(eventId)
				? prev.selectedEvents.filter(id => id !== eventId)
				: [...prev.selectedEvents, eventId]
		}))
	}

	const validateAndGetErrors = () => {
		const urlErr = getUrlValidationError(state.url)

		setState(prev => ({
			...prev,
			urlErrorMessage: urlErr
		}))

		return {urlErr}
	}

	const setApiKeyErrorMessage = (error: string | null) => {
		setState(prev => ({...prev, apiKeyErrorMessage: error}))
	}

	return {
		...state,
		resetForm,
		setUrl,
		setApiKey,
		setSelectedEvents,
		toggleEvent,
		validateAndGetErrors,
		setApiKeyErrorMessage,
		setFormPopulatedForEdit: (value: boolean) =>
			setState(prev => ({...prev, formPopulatedForEdit: value}))
	}
}

export type UseWebhookFormReturn = ReturnType<typeof useWebhookForm>
